import { Koi } from '@ksyos/koi';
import type { Section } from '@common/formEngine/formEngine.types';
import type { GenericData } from '@common/formEngine/generic/generic.types';
import type { NullablePrimitives, ValueOf } from '@common/utils/utilityTypes';
import type { IPatientAddEditValidated } from '@common/validation/patient';
import type { ProcessActivityAsset } from '@common/validation/processActivityAsset';
import type { Quantity } from '@common/validation/quantity';
import { dependsOn, valueDependsOn } from '@common/formEngine/dependsOn';
import { patient_verzekeringskosten_akkoord_field } from '@common/formEngine/generic/genericFields';
import { emailSchema } from '@common/validation/schemas';
import { getTextInputStringSchema } from '@common/validation/utils/getTextInputStringSchema';
import type {
    AlcoholconsumptieGebruiksgedrag,
    DrugsgebruikGebruiksgedrag,
    Locatie,
    RokenGebruiksgedrag,
} from '../../neurology';
import {
    alcoholconsumptieNeeGebruiksgedragOptions,
    drugsgebruikNeeGebruiksgedragOptions,
    locatieOptions,
    rokenJaGebruiksgedragOptions,
    rokenNeeGebruiksgedragOptions,
    thuissituatieOptions,
} from '../../neurology';

// Beeldvorming type
export const BeeldvormingType = {
    anders: 'Anders, namelijk...',
    ct_scan: 'CT-scan',
    mri: 'MRI',
    rontgen: 'Röntgenscan',
    rugoperatie: 'Rugoperatie',
} as const;
export type BeeldvormingType = ValueOf<typeof BeeldvormingType>;
export const beeldvormingTypeOptions: BeeldvormingType[] = [
    BeeldvormingType.mri,
    BeeldvormingType.rontgen,
    BeeldvormingType.ct_scan,
    BeeldvormingType.rugoperatie,
    BeeldvormingType.anders,
];

// Comorbiditeiten type
export const ComorbiditeitenType = {
    anders: 'Anders, namelijk...',
    artrose_heup_knie_rug: 'Artrose heup / knie / rug',
    diabetes: 'Diabetes',
    osteoporose: 'Osteoporose',
    overgewicht_obesitas: 'Overgewicht / obesitas',
    psychische_problemen: 'Psychische problemen',
    scoliose: 'Scoliose',
} as const;
export type ComorbiditeitenType = ValueOf<typeof ComorbiditeitenType>;
export const comorbiditeitenTypeOptions: ComorbiditeitenType[] = [
    ComorbiditeitenType.psychische_problemen,
    ComorbiditeitenType.overgewicht_obesitas,
    ComorbiditeitenType.artrose_heup_knie_rug,
    ComorbiditeitenType.scoliose,
    ComorbiditeitenType.osteoporose,
    ComorbiditeitenType.diabetes,
    ComorbiditeitenType.anders,
];

// Conservatieve behandelingen type
export const ConservatieveBehandelingenType = {
    anders: 'Anders, namelijk...',
    chiropractie: 'Chiropractie',
    ergotherapie: 'Ergotherapie',
    fysio_manueel_therapie: 'Fysio- / manueel therapie',
    oefentherapie: 'Oefentherapie',
    osteopathie: 'Osteopathie',
} as const;
export type ConservatieveBehandelingenType = ValueOf<typeof ConservatieveBehandelingenType>;
export const conservatieveBehandelingenTypeOptions: ConservatieveBehandelingenType[] = [
    ConservatieveBehandelingenType.fysio_manueel_therapie,
    ConservatieveBehandelingenType.ergotherapie,
    ConservatieveBehandelingenType.oefentherapie,
    ConservatieveBehandelingenType.chiropractie,
    ConservatieveBehandelingenType.osteopathie,
    ConservatieveBehandelingenType.anders,
];

// Specialist type
export const SpecialistType = {
    anders: 'Anders, namelijk...',
    anesthesioloog: 'Anesthesioloog',
    neuroloog_neurochirurg: 'Neuroloog / neurochirurg',
    orthopeed_orthopedisch_chirurg: 'Orthopeed / orthopedisch chirurg',
} as const;
export type SpecialistType = ValueOf<typeof SpecialistType>;
export const specialistTypeOptions: SpecialistType[] = [
    SpecialistType.neuroloog_neurochirurg,
    SpecialistType.orthopeed_orthopedisch_chirurg,
    SpecialistType.anesthesioloog,
    SpecialistType.anders,
];

export type LumbarRadiculopathyReferralData = NullablePrimitives<{
    alcoholconsumptie: boolean;
    alcoholconsumptie_gebruiksgedrag: AlcoholconsumptieGebruiksgedrag;
    assets: ProcessActivityAsset[];
    baan: string;
    beeldvorming_of_operatie_ondergaan_tijdens_eerdere_of_huidige_episode: boolean;
    beeldvorming_of_operatie_ondergaan_tijdens_eerdere_of_huidige_episode_type: NullablePrimitives<{
        anders: string;
        ct_scan: boolean;
        mri: boolean;
        rontgen: boolean;
        rugoperatie: boolean;
    }>;
    comorbiditeiten: boolean;
    comorbiditeiten_type: NullablePrimitives<{
        anders: string;
        artrose_heup_knie_rug: boolean;
        diabetes: boolean;
        osteoporose: boolean;
        overgewicht_obesitas: boolean;
        psychische_problemen: boolean;
        scoliose: boolean;
    }>;
    conservatieve_behandelingen: boolean;
    conservatieve_behandelingen_type: NullablePrimitives<{
        anders: string;
        chiropractie: boolean;
        ergotherapie: boolean;
        fysio_manueel_therapie: boolean;
        oefentherapie: boolean;
        osteopathie: boolean;
    }>;
    drugsgebruik: boolean;
    drugsgebruik_gebruiksgedrag: DrugsgebruikGebruiksgedrag;
    eerdere_episodes_van_lage_rugpijn_bekend: boolean;
    eerdere_episodes_van_lage_rugpijn_bekend_wanneer: Date | string;
    exclusiecriteria_gelezen: boolean;
    huidige_klachten_toelichting: string;
    huidige_medicatiegebruik: string;
    hulpvraag_patient: string;
    lichamelijke_activiteit: Quantity;
    locatie_rugpijn: Locatie;
    locatie_uitstraling: Locatie;
    medische_voorgeschiedenis_belangrijke_uitkomsten: string;
    patient: Pick<IPatientAddEditValidated, 'emailAddress'>;
    roken: boolean;
    roken_gebruiksgedrag: RokenGebruiksgedrag;
    specialist_gezien_voor_eerdere_of_huidige_episode: boolean;
    specialist_gezien_voor_eerdere_of_huidige_episode_type: NullablePrimitives<{
        anders: string;
        anesthesioloog: boolean;
        neuroloog_neurochirurg: boolean;
        orthopeed_orthopedisch_chirurg: boolean;
    }>;
    start_klachten: Date | string;
    telefoonnummer: string;
    thuissituatie: string;
    toestemming_patient_voor_benaderen_feedback: boolean;
    vraag_aan_neuroloog: string;
    zonder_ksyos_doorverwezen: boolean;
}> &
    Pick<GenericData, 'patient_verzekeringskosten_akkoord'>;

export const lumbarRadiculopathyReferralSections: Section<LumbarRadiculopathyReferralData>[] = [
    {
        fields: [
            patient_verzekeringskosten_akkoord_field,
            {
                draftSchema: Koi.boolean().allow(null),
                finalSchema: Koi.boolean().valid(true),
                initialValue: null,
                isRequired: true,
                key: 'exclusiecriteria_gelezen',
                label: 'Ik heb de exclusiecriteria gelezen en ga akkoord',
                type: 'custom',
            },
        ],
    },
    {
        fields: [],
        subsections: [
            {
                fields: [
                    {
                        isRequired: true,
                        key: 'hulpvraag_patient',
                        label: 'Hulpvraag patiënt',
                        type: 'textArea',
                    },
                    {
                        draftSchema: Koi.koi().date().allow(null),
                        finalSchema: Koi.koi().date(),
                        isRequired: true,
                        key: 'start_klachten',
                        label: 'Start klachten',
                        type: 'dateInput',
                    },
                    {
                        isRequired: true,
                        key: 'locatie_rugpijn',
                        label: 'Locatie rugpijn',
                        type: 'radioGroup',
                        valueOptions: locatieOptions,
                    },
                    {
                        isRequired: true,
                        key: 'locatie_uitstraling',
                        label: 'Locatie uitstraling',
                        type: 'radioGroup',
                        valueOptions: locatieOptions,
                    },
                    {
                        key: 'huidige_klachten_toelichting',
                        label: 'Toelichting',
                        type: 'textArea',
                    },
                ],
                title: 'Huidige klachten',
            },
            {
                fields: [
                    {
                        allowOtherOption: true,
                        isRequired: true,
                        key: 'thuissituatie',
                        label: 'Thuissituatie',
                        type: 'radioGroup',
                        valueOptions: thuissituatieOptions,
                    },
                    {
                        isRequired: true,
                        key: 'baan',
                        label: 'Baan',
                        type: 'textInput',
                    },
                    {
                        defaultUnit: 'uren per week',
                        isRequired: true,
                        key: 'lichamelijke_activiteit',
                        label: 'Lichamelijke activiteit',
                        minValue: 0,
                        type: 'quantity',
                        units: ['minuten per week', 'uren per week'],
                    },
                ],
                title: 'Sociale anamnese',
            },
            {
                fields: [
                    {
                        isRequired: true,
                        key: 'alcoholconsumptie',
                        label: 'Alcoholconsumptie',
                        type: 'boolean',
                    },
                    {
                        include: dependsOn('alcoholconsumptie', ({ alcoholconsumptie }) => alcoholconsumptie === false),
                        isRequired: true,
                        key: 'alcoholconsumptie_gebruiksgedrag',
                        label: 'Gebruiksgedrag',
                        type: 'radioGroup',
                        valueOptions: alcoholconsumptieNeeGebruiksgedragOptions,
                    },
                    {
                        isRequired: true,
                        key: 'roken',
                        label: 'Roken',
                        type: 'boolean',
                    },
                    {
                        draftSchema: Koi.when('roken', {
                            is: true,
                            otherwise: Koi.valid(...rokenNeeGebruiksgedragOptions).allow(null),
                            then: Koi.valid(...rokenJaGebruiksgedragOptions).allow(null),
                        }),
                        finalSchema: Koi.when('roken', {
                            is: true,
                            otherwise: Koi.valid(...rokenNeeGebruiksgedragOptions),
                            then: Koi.valid(...rokenJaGebruiksgedragOptions),
                        }),
                        include: dependsOn('roken', ({ roken }) => roken !== null),
                        isRequired: true,
                        key: 'roken_gebruiksgedrag',
                        label: 'Gebruiksgedrag',
                        type: 'radioGroup',
                        valueOptions: valueDependsOn('roken', ({ roken }) =>
                            roken === true ? rokenJaGebruiksgedragOptions : rokenNeeGebruiksgedragOptions
                        ),
                    },
                    {
                        isRequired: true,
                        key: 'drugsgebruik',
                        label: 'Drugsgebruik',
                        type: 'boolean',
                    },
                    {
                        include: dependsOn('drugsgebruik', ({ drugsgebruik }) => drugsgebruik === false),
                        isRequired: true,
                        key: 'drugsgebruik_gebruiksgedrag',
                        label: 'Gebruiksgedrag',
                        type: 'radioGroup',
                        valueOptions: drugsgebruikNeeGebruiksgedragOptions,
                    },
                ],
                title: 'Leefstijl',
            },
            {
                fields: [
                    {
                        key: 'huidige_medicatiegebruik',
                        label: 'Huidige medicatiegebruik',
                        type: 'textArea',
                    },
                    {
                        isRequired: true,
                        key: 'comorbiditeiten',
                        label: 'Comorbiditeiten',
                        type: 'boolean',
                    },
                    {
                        checkboxEnumType: ComorbiditeitenType,
                        include: dependsOn('comorbiditeiten'),
                        isRequired: true,
                        key: 'comorbiditeiten_type',
                        label: 'Type comorbiditeiten',
                        type: 'checkboxGroup',
                        valueOptions: comorbiditeitenTypeOptions,
                    },
                    {
                        isRequired: true,
                        key: 'conservatieve_behandelingen',
                        label: 'Conservatieve behandelingen',
                        type: 'boolean',
                    },
                    {
                        checkboxEnumType: ConservatieveBehandelingenType,
                        include: dependsOn('conservatieve_behandelingen'),
                        isRequired: true,
                        key: 'conservatieve_behandelingen_type',
                        label: 'Type behandelingen',
                        type: 'checkboxGroup',
                        valueOptions: conservatieveBehandelingenTypeOptions,
                    },
                    {
                        isRequired: true,
                        key: 'eerdere_episodes_van_lage_rugpijn_bekend',
                        label: 'Eerdere episodes van lage rugpijn bekend',
                        type: 'boolean',
                    },
                    {
                        draftSchema: Koi.when('eerdere_episodes_van_lage_rugpijn_bekend', {
                            is: true,
                            otherwise: Koi.valid(null),
                            then: Koi.koi().date().allow(null),
                        }),
                        finalSchema: Koi.when('eerdere_episodes_van_lage_rugpijn_bekend', {
                            is: true,
                            otherwise: Koi.valid(null),
                            then: Koi.koi().date(),
                        }),
                        include: dependsOn('eerdere_episodes_van_lage_rugpijn_bekend'),
                        isRequired: true,
                        key: 'eerdere_episodes_van_lage_rugpijn_bekend_wanneer',
                        label: 'Wanneer',
                        type: 'monthInput',
                    },
                    {
                        isRequired: true,
                        key: 'specialist_gezien_voor_eerdere_of_huidige_episode',
                        label: 'Specialist gezien voor eerdere of huidige episode',
                        type: 'boolean',
                    },
                    {
                        checkboxEnumType: SpecialistType,
                        include: dependsOn('specialist_gezien_voor_eerdere_of_huidige_episode'),
                        isRequired: true,
                        key: 'specialist_gezien_voor_eerdere_of_huidige_episode_type',
                        label: 'Type specialist',
                        type: 'checkboxGroup',
                        valueOptions: specialistTypeOptions,
                    },
                    {
                        isRequired: true,
                        key: 'beeldvorming_of_operatie_ondergaan_tijdens_eerdere_of_huidige_episode',
                        label: 'Beeldvorming of operatie ondergaan tijdens eerdere of huidige episode',
                        type: 'boolean',
                    },
                    {
                        checkboxEnumType: BeeldvormingType,
                        include: dependsOn('beeldvorming_of_operatie_ondergaan_tijdens_eerdere_of_huidige_episode'),
                        isRequired: true,
                        key: 'beeldvorming_of_operatie_ondergaan_tijdens_eerdere_of_huidige_episode_type',
                        label: 'Type beeldvorming of operatie',
                        type: 'checkboxGroup',
                        valueOptions: beeldvormingTypeOptions,
                    },
                    {
                        include: dependsOn(
                            [
                                'beeldvorming_of_operatie_ondergaan_tijdens_eerdere_of_huidige_episode',
                                'conservatieve_behandelingen',
                                'specialist_gezien_voor_eerdere_of_huidige_episode',
                            ],
                            ({
                                beeldvorming_of_operatie_ondergaan_tijdens_eerdere_of_huidige_episode,
                                conservatieve_behandelingen,
                                specialist_gezien_voor_eerdere_of_huidige_episode,
                            }) =>
                                beeldvorming_of_operatie_ondergaan_tijdens_eerdere_of_huidige_episode === true ||
                                conservatieve_behandelingen === true ||
                                specialist_gezien_voor_eerdere_of_huidige_episode === true
                        ),
                        key: 'medische_voorgeschiedenis_belangrijke_uitkomsten',
                        label: 'Belangrijke uitkomsten van medische voorgeschiedenis',
                        type: 'textArea',
                    },
                ],
                title: 'Medische voorgeschiedenis',
            },
            {
                fields: [
                    {
                        key: 'assets',
                        label: 'Upload bestanden',
                        type: 'assets',
                    },
                ],
                title: 'Relevante bestanden',
            },
        ],
        title: 'Anamnese',
    },
    {
        fields: [
            {
                key: 'vraag_aan_neuroloog',
                label: 'Vraag aan neuroloog',
                type: 'textArea',
            },
        ],
        title: 'Overige vragen',
    },
    {
        fields: [
            {
                // TODO: CS-23832 Make this generic
                draftSchema: getTextInputStringSchema(true, 255),
                finalSchema: emailSchema.allow(null),
                key: 'patient.emailAddress',
                label: 'E-mailadres',
                type: 'textInput',
            },
            {
                isRequired: true,
                key: 'telefoonnummer',
                label: 'Telefoonnummer',
                type: 'textInput',
            },
        ],
        title: 'Contactgegevens patiënt',
    },
    {
        fields: [
            {
                key: 'toestemming_patient_voor_benaderen_feedback',
                label: 'Toestemming patiënt voor benaderen feedback',
                type: 'boolean',
            },
        ],
        title: 'Toestemming patiënt',
    },
    {
        fields: [
            {
                key: 'zonder_ksyos_doorverwezen',
                label: 'Had je deze patiënt zonder Ksyos doorverwezen naar de specialist?',
                tooltipText:
                    'Deze informatie verzamelen we omdat we willen weten of Ksyos onnodige verwijzingen voorkomt.',
                type: 'boolean',
            },
        ],
        title: 'Overig',
    },
];
